import { useCallback, useEffect, useState } from 'react';
import { ArrowSquareOut, Info, TrendUp, Warning } from 'shared/icons';
import { FormatMoney } from '../../utils/format';
import { ItemId, Mono, Sku } from '../ui/Mono';
import { MLB_URLS } from '../../utils/ml';
import api from '../../services/api';

/**
 * RADAR DE REBATE.
 *
 * O Mercado Livre roda várias campanhas cofinanciadas ao mesmo tempo e o mesmo
 * anúncio fica candidato em mais de uma, cada uma com um rebate diferente. No
 * painel do ML isso só aparece clicando anúncio por anúncio, e às vezes só
 * quando se tenta REMOVER a campanha — foi assim que o dono descobriu.
 *
 * A aba "Melhores Campanhas" responde outra pergunta: ela usa Pareto, e só
 * sugere a troca que não piora NEM o líquido NEM o preço ao consumidor. Como a
 * campanha que paga mais rebate quase sempre vem com desconto mais fundo, ela
 * costuma reprovar no Pareto e sumir da tela. Medido: de 39 pares com rebate
 * maior, 13 passavam e 26 ficavam invisíveis.
 *
 * Aqui a ordem é por diferença de rebate, e o efeito no líquido aparece AO
 * LADO em vez de esconder a linha. A decisão continua sendo do dono: esta tela
 * não aceita campanha, só mostra onde vale olhar.
 */

const DASH = '—';

/** Ganho em pontos percentuais. Sempre com sinal, porque a leitura é relativa. */
function FormatPp(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return DASH;
    return `${n > 0 ? '+' : ''}${n.toFixed(2).replace('.', ',')} pp`;
}

/** Delta em dinheiro: verde quando melhora para o vendedor, vermelho quando piora. */
function MoneyDelta({ value, invert = false }) {
    const n = Number(value);
    if (!Number.isFinite(n)) {
        return <span className="text-mono-400">{DASH}</span>;
    }
    const bom = invert ? n <= 0 : n >= 0;
    const cor = n === 0 ? 'text-mono-500 dark:text-mono-400' : bom ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400';
    return (
        <span className={`font-mono tabular-nums ${cor}`}>
            {n > 0 ? '+' : ''}
            {FormatMoney(n)}
        </span>
    );
}

const RebateOpportunities = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [minGain, setMinGain] = useState(0);
    const [somenteEscondidas, setSomenteEscondidas] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.getRebateOpportunities({ limit: 100, offset: 0, min_gain_pp: minGain });
            setData(res);
        } catch (err) {
            setError(err?.message || 'Não foi possível carregar as oportunidades.');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [minGain]);

    useEffect(() => {
        load();
    }, [load]);

    const itens = (data?.items || []).filter((item) => (somenteEscondidas ? !item.passes_pareto : true));
    const escondidas = (data?.items || []).filter((item) => !item.passes_pareto).length;

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
                <label className="text-sm text-mono-700 dark:text-mono-300">
                    Ganho mínimo
                    <select
                        value={minGain}
                        onChange={(event) => setMinGain(Number(event.target.value))}
                        className="ml-2 rounded-lg border border-mono-300 bg-white px-2 py-1 text-sm dark:border-mono-700 dark:bg-mono-900 dark:text-white"
                    >
                        <option value={0}>qualquer</option>
                        <option value={1}>1 pp</option>
                        <option value={2}>2 pp</option>
                        <option value={3}>3 pp</option>
                    </select>
                </label>

                <label className="flex items-center gap-2 text-sm text-mono-700 dark:text-mono-300">
                    <input
                        type="checkbox"
                        checked={somenteEscondidas}
                        onChange={(event) => setSomenteEscondidas(event.target.checked)}
                        className="rounded border-mono-300 dark:border-mono-700"
                    />
                    Só as que o Pareto esconde ({escondidas})
                </label>

                {data ? (
                    <span className="text-sm text-mono-500 dark:text-mono-400">
                        {data.paging.total} {data.paging.total === 1 ? 'anúncio' : 'anúncios'} com oportunidade
                    </span>
                ) : null}
            </div>

            <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-200">
                <Info size={16} />
                <span>
                    Esta tela é só leitura. Ela mostra onde o Mercado Livre paga mais rebate do que na campanha
                    atual do anúncio. A adesão continua sendo feita no painel do Mercado Livre.{' '}
                    <strong>Líquido</strong> negativo significa que o rebate maior vem com desconto mais fundo e você
                    recebe menos por venda, mesmo o Mercado Livre bancando uma fatia maior.
                </span>
            </div>

            {error ? (
                <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
                    <Warning size={18} />
                    <span>{error}</span>
                </div>
            ) : null}

            {loading ? <p className="text-sm text-mono-500 dark:text-mono-400">Carregando…</p> : null}

            {!loading && !error && itens.length === 0 ? (
                <p className="rounded-xl border border-mono-200 bg-white p-6 text-sm text-mono-600 dark:border-mono-800 dark:bg-mono-950 dark:text-mono-400">
                    Nenhuma candidatura paga mais rebate que a campanha atual com esse recorte.
                </p>
            ) : null}

            {itens.length > 0 ? (
                <div className="overflow-x-auto rounded-xl border border-mono-200 bg-white dark:border-mono-800 dark:bg-mono-950">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-mono-200 bg-mono-50 text-left text-xs uppercase tracking-wide text-mono-500 dark:border-mono-800 dark:bg-mono-900 dark:text-mono-400">
                                <th className="px-3 py-2">Anúncio</th>
                                <th className="px-3 py-2">Campanha atual</th>
                                <th className="px-3 py-2">Candidatura</th>
                                <th className="px-3 py-2 text-right">Ganho</th>
                                <th className="px-3 py-2 text-right">Líquido</th>
                                <th className="px-3 py-2 text-right">Consumidor</th>
                                <th className="px-3 py-2">Hoje</th>
                            </tr>
                        </thead>
                        <tbody>
                            {itens.map((item) => (
                                <tr
                                    key={`${item.item_id}-${item.target.campaign_id}`}
                                    className="border-b border-mono-100 last:border-0 dark:border-mono-900"
                                >
                                    <td className="px-3 py-2">
                                        <div className="flex items-start gap-2">
                                            {item.thumbnail ? (
                                                <img
                                                    src={item.thumbnail}
                                                    alt=""
                                                    className="h-9 w-9 flex-none rounded object-cover"
                                                />
                                            ) : null}
                                            <div className="min-w-0">
                                                <p className="truncate text-xs font-medium text-black dark:text-white">
                                                    {item.title || DASH}
                                                </p>
                                                <div className="flex items-center gap-2 text-[11px]">
                                                    <ItemId value={item.item_id} permalink={item.permalink} />
                                                    {item.seller_sku ? <Sku value={item.seller_sku} /> : null}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <p className="truncate text-xs text-mono-700 dark:text-mono-300">
                                            {item.current.campaign_name || item.current.campaign_id}
                                        </p>
                                        <Mono className="text-[11px] text-mono-500 dark:text-mono-400">
                                            {item.current.campaign_type} · {item.current.rebate_percent}%
                                        </Mono>
                                    </td>
                                    <td className="px-3 py-2">
                                        <p className="truncate text-xs text-mono-700 dark:text-mono-300">
                                            {item.target.campaign_name || item.target.campaign_id}
                                        </p>
                                        <Mono className="text-[11px] text-mono-500 dark:text-mono-400">
                                            {item.target.campaign_type} · {item.target.rebate_percent}% ·{' '}
                                            {item.target.item_status}
                                        </Mono>
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <span className="font-mono tabular-nums font-semibold text-green-700 dark:text-green-400">
                                            {FormatPp(item.rebate_gain_pp)}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <MoneyDelta value={item.net_delta} />
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <MoneyDelta value={item.consumer_delta} invert />
                                    </td>
                                    <td className="px-3 py-2">
                                        {item.passes_pareto ? (
                                            <span className="rounded bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-800 dark:bg-green-900 dark:text-green-200">
                                                já sugerida
                                            </span>
                                        ) : (
                                            <span className="rounded bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                                                invisível hoje
                                            </span>
                                        )}
                                        <a
                                            href={MLB_URLS.promos(item.item_id)}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="ml-2 inline-flex items-center gap-1 text-[11px] text-blue-700 hover:underline dark:text-blue-300"
                                        >
                                            abrir no ML <ArrowSquareOut size={11} />
                                        </a>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}

            {itens.length > 0 ? (
                <p className="flex items-center gap-2 text-xs text-mono-500 dark:text-mono-400">
                    <TrendUp size={14} />
                    Ordenado pela maior diferença de rebate. Quando o mesmo anúncio tem mais de uma candidatura
                    melhor, a linha mostra a maior delas.
                </p>
            ) : null}
        </div>
    );
};

export default RebateOpportunities;
